import React, {
  useCallback, useEffect, useMemo, useState, Fragment, ReactElement,
} from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import StatusBar from '../../../components/status-bar/status-bar.component';
import LimitedPageContainer from '../../../components/limited-page-block/limited-page-container.component';
import Loader from '../../../components/loader/loader.component';
import PullToRefresh from '../../../components/pull-to-refresh/pull-to-refresh.component';
import ContentHeader from '../../../components/content-header/content-header.component';
import LeaveConfirmModal from '../../../components/leave-confirm-modal/leave-confirm-modal.component';
import Form from '../../../components/form/form.component';
import FormError from '../../../components/form-error/form-error.component';
import FormContainer from '../../../components/form-container/form-container.component';
import Controls from '../../app/controls/controls.component';
import SettingsPageTabs from '../settings-page-tabs/settings-page-tabs.component';
import SettingsUserNotifications
  from '../settings-user/settings-user-internal/settings-user-notifications/settings-user-notifications.component';
import FormService from '../../../services/form-service';
import useServerFormErrors from '../../../hooks/use-server-form-errors';
import useEditModeQuery from '../../../hooks/use-edit-mode-query';
import useFormBlock from '../../../hooks/use-form-block';
import useFormStructureModified from '../../../hooks/use-form-structure-modified';
import saveNotificationSettings from './settings-notifications.action';
import { toggleEditMode } from '../../app/controls/controls.action';
import { getCommentListData } from '../../app/comments/comments.action';
import { getNotificationListData } from '../../app/notifications/notifications.action';
import { getSettingsData } from '../settings.action';
import { getListDealData } from '../../app/search-list/search-list.action';
import { resetFormErrors } from '../../app/app.action';
import { FORM_ID_SETTINGS_NOTIFICATIONS } from '../../../constants/system/form-ids';
import { SETTINGS_NOTIFICATION_EDIT } from '../../../constants/entity/permissions';
import { TAB_NAME_NOTIFICATIONS } from '../../../constants/system/tab-names';

const SettingsNotifications: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const {
    isEditMode,
    isDataLoaded,
    isRightDrawerOpen,
    currentUser,
    portfolioIdsFilter,
    portfolioGroupIdsFilter,
    notificationSettingsData,
    notificationTypeSettingsData,
    commentTypeSettingsData,
    dealList,
    portfolioList,
    form: formProps,
    modified: formModifiedFields,
    handleSubmit: submitForm,
    pristine: isFormPristine,
    isSubmitting: isFormSaving,
    error: formError,
    values: formValues,
    hasValidationErrors: withFormErrors,
    formErrors: serverFormErrors,
    mainFormError: mainServerFormError,
  } = props;

  const {
    reset: resetForm,
    change: changeForm,
  } = formProps;

  const dispatch: ReduxDispatch = useDispatch();
  const navigate: ReactRouterNavigate = useNavigate();
  const isFormStructureModified: boolean = useFormStructureModified(formModifiedFields, isDataLoaded);

  const [isOpenMessage, setIsOpenMessage]: [boolean, Function] = useState(false);
  const [statusMessage, setStatusMessage]: [string, Function] = useState('');
  const [selectedDeals, setSelectedDeals]: [Deal | null, Function] = useState(null);
  const [selectedPortfolios, setSelectedPortfolios]: [Portfolio | null, Function] = useState(null);
  const [visibleOptions, setVisibleOptions]: [number[] | null, Function] = useState(null);
  const [isLeaveConfirmed, setIsLeaveConfirmed]: [boolean, Function] = useState(false);
  const [isLeaveConfirmModalOpen, setIsLeaveConfirmModalOpen]: [boolean, Function] = useState(false);
  const [lastLocation, setLastLocation]: [ReactRouterLocation | null, Function] = useState<StateValueDefault>(null);

  const settingsNotificationEditPermissions: string[] = useMemo((): string[] => [SETTINGS_NOTIFICATION_EDIT], []);

  const pageTitle: string = 'Settings: Notifications';
  const isFormDataPristine: boolean = isFormPristine;
  const isFormChanged: boolean = isEditMode && !isFormDataPristine;
  const isFormStateChanged: boolean = isEditMode;
  const isSaveDisabled: boolean = isFormSaving || (isFormDataPristine && !isFormStructureModified);
  const isEditModeQueryDisabled: boolean = isLeaveConfirmed && !!lastLocation;
  const withServerErrors: boolean = serverFormErrors && !!Object.keys(serverFormErrors).length;

  const onDealSearch: Function = useCallback((query: string): void => {
    dispatch(getListDealData({
      query,
    }));
  }, []);

  const onDealSearchClose: Function = useCallback((): void => {
    dispatch(getListDealData({
      query: null,
    }));
  }, []);

  const onCancelForm: Function = useCallback((): void => {
    FormService.forceResetForm(resetForm, formProps);

    resetSelectedDeals();
    resetSelectedPortfolios();
    resetVisibleOptions();
    dispatch(toggleEditMode(false));
  }, []);

  const onLeaveConfirm: Function = useCallback((): void => {
    setIsLeaveConfirmed(true);
    setIsLeaveConfirmModalOpen(false);

    onCancelForm();
  }, []);

  const closeLeaveConfirmModal: Function = useCallback((): void => {
    setIsLeaveConfirmModalOpen(false);
  }, []);

  const getOptions: Function = useCallback((): RequestOptions => {
    let options: RequestOptions = {};

    options = portfolioIdsFilter && portfolioIdsFilter.length
      ? { ...options, portfolioIds: portfolioIdsFilter } : options;
    options = portfolioGroupIdsFilter && portfolioGroupIdsFilter.length
      ? { ...options, portfolioGroupIds: portfolioGroupIdsFilter } : options;

    return options;
  }, [portfolioIdsFilter, portfolioGroupIdsFilter]);

  const sendForm: Function = useCallback((values: FormValues): Promise<ActionCallback> | null => {
    if (!values) {
      return null;
    }

    const notificationForm: FormValueItem = {
      ...values,
    };

    return new Promise((resolve: ActionResolveCallback): void => {
      dispatch(saveNotificationSettings({
        notificationSettingsForm: {
          notificationSettingsData: Object.keys(notificationForm.notificationSettings).map((
            settingName: string,
          ): SettingInput => {
            const notificationSettingsItem: SettingInput = notificationForm.notificationSettings[settingName];
            const notificationSettingOptions: JsonObject | null = notificationSettingsItem.options
            && (notificationSettingsItem.options.dealIds || notificationSettingsItem.options.portfolioIds) ? {} : null;

            if (notificationSettingOptions && notificationSettingsItem.options.dealIds) {
              notificationSettingOptions.dealIds = notificationSettingsItem.value
                ? notificationSettingsItem.options.dealIds
                  .map((dealItem: Deal): number => dealItem.id) : [];
            }

            if (notificationSettingOptions && notificationSettingsItem.options.portfolioIds) {
              notificationSettingOptions.portfolioIds = notificationSettingsItem.value
                ? notificationSettingsItem.options.portfolioIds
                  .map((portfolioItem: Portfolio): number => portfolioItem.id) : [];
            }

            return {
              name: settingName,
              value: notificationSettingsItem.value ? notificationSettingsItem.value.toString() : null,
              options: notificationSettingOptions,
            };
          }),
          notificationTypeSettingsData: Object.keys(notificationForm.notificationTypeSettings).map((
            settingName: string,
          ): SettingInput => {
            const notificationTypeSettingsItem: SettingInput = notificationForm.notificationTypeSettings[settingName];

            return {
              name: settingName,
              value: notificationTypeSettingsItem.value ? notificationTypeSettingsItem.value.toString() : null,
            };
          }),
          commentTypeSettingsData: Object.keys(notificationForm.commentTypeSettings)
            .map((settingName: string): SettingInput => {
              const commentTypeSettingsItem: SettingInput = notificationForm.commentTypeSettings[settingName];

              return {
                name: settingName,
                value: commentTypeSettingsItem.value ? commentTypeSettingsItem.value.toString() : null,
              };
            }),
        },
      }, (response: ActionResponse): void => {
        onCancelForm();

        dispatch(getCommentListData(getOptions()));
        dispatch(getNotificationListData(getOptions()));

        setStatusMessage('Saved successfully');
        setIsOpenMessage(true);

        resolve(response);
      }, (exception: ServerError): void => {
        if (exception && exception.message) {
          submitForm();
        }
      }));
    });
  }, [isSaveDisabled, portfolioIdsFilter, portfolioGroupIdsFilter]);

  const onSubmitForm: Function = useCallback((): void => {
    submitForm();

    if (!withFormErrors) {
      sendForm(formValues);
    } else {
      FormService.focusOnFirstFormError();
    }
  }, [isSaveDisabled, portfolioIdsFilter, portfolioGroupIdsFilter, formValues, withFormErrors]);

  const closeMessage: Function = useCallback((): void => {
    setIsOpenMessage(false);
    setStatusMessage('');
  }, []);

  const resetSelectedDeals: Function = useCallback((): void => {
    if (notificationSettingsData) {
      setSelectedDeals(
        notificationSettingsData.reduce((resultSelectedDeals: Deal[], notificationSettingItem: Setting): Deal[] => (
          notificationSettingItem.options && notificationSettingItem.options.dealIds
          && notificationSettingItem.options.dealIds.length
            ? {
              ...resultSelectedDeals,
              [notificationSettingItem.id]: notificationSettingItem.options.dealIds.map((
                dealId: number,
              ): Deal | null => (
                dealList && dealList.length ? dealList.find((dealItem: Deal): boolean => dealItem.id === dealId) : null
              )).filter((dealItem: Deal): boolean => !!dealItem),
            } : resultSelectedDeals
        ), {}),
      );
    }
  }, [notificationSettingsData, dealList]);

  const resetSelectedPortfolios: Function = useCallback((): void => {
    if (notificationSettingsData) {
      setSelectedPortfolios(
        notificationSettingsData.reduce((
          resultSelectedPortfolios: Portfolio, notificationSettingItem: Setting,
        ): Portfolio => (
          notificationSettingItem.options && notificationSettingItem.options.portfolioIds
          && notificationSettingItem.options.portfolioIds.length
            ? {
              ...resultSelectedPortfolios,
              [notificationSettingItem.id]: notificationSettingItem.options.portfolioIds.map((
                portfolioId: number,
              ): Portfolio | null => (
                portfolioList && portfolioList.length
                  ? portfolioList.find((portfolioItem: Portfolio): boolean => portfolioItem.id === portfolioId) : null
              )).filter((portfolioItem: Portfolio): boolean => !!portfolioItem),
            } : resultSelectedPortfolios
        ), {}),
      );
    }
  }, [notificationSettingsData, portfolioList]);

  const resetVisibleOptions: Function = useCallback((): void => {
    if (notificationSettingsData) {
      setVisibleOptions(
        notificationSettingsData.filter((settingItem: Setting): boolean => !!settingItem.value)
          .map((settingItem: Setting): number => settingItem.id),
      );
    }
  }, [notificationSettingsData]);

  const onFormLeave: FormLeaveFunction = useCallback((nextLocation: ReactRouterLocation): boolean => {
    if (isFormChanged) {
      setIsLeaveConfirmModalOpen(true);

      setLastLocation(nextLocation);

      return false;
    }

    if (isEditMode) {
      dispatch(toggleEditMode(false));
    }

    return true;
  }, [isFormChanged, isEditMode]);

  const onBeforeLocationChange: Function = useCallback((nextLocation: ReactRouterLocation): void => {
    if (isFormChanged) {
      setIsLeaveConfirmModalOpen(true);

      setLastLocation(nextLocation);
    }
  }, [isFormChanged]);

  const onOptionDealsChange: Function = useCallback((
    dealValues: Deal[], settingName: string, settingId: number,
  ): void => {
    setSelectedDeals({
      ...(selectedDeals || {}),
      [settingId]: dealValues,
    });
    changeForm(`${settingName}.options.dealIds`, dealValues);
  }, [selectedDeals]);

  const onOptionPortfoliosChange: Function = useCallback((
    portfolioValues: Portfolio[], settingName: string, settingId: number,
  ): void => {
    setSelectedPortfolios({
      ...(selectedPortfolios || {}),
      [settingId]: portfolioValues,
    });
    changeForm(`${settingName}.options.portfolioIds`, portfolioValues);
  }, [selectedPortfolios]);

  const onVisibleOptionsChange: Function = useCallback((newVisibleOptions: number[]): void => {
    setVisibleOptions(newVisibleOptions);
  }, []);

  const onRefresh: Function = useCallback((): void => {
    dispatch(getSettingsData());
  }, []);

  const onFormChange: Function = useCallback((event: React.SyntheticEvent): void => {
    const fieldName: string = event.target && (event.target as HTMLInputElement).name;

    if (withServerErrors && fieldName) {
      dispatch(resetFormErrors());
      FormService.resetFormServerErrors(formProps, fieldName);
      submitForm();
    }
  }, [withServerErrors, formProps]);

  useEffect((): void => {
    dispatch(getSettingsData());
  }, []);

  useEffect((): void => {
    if (notificationSettingsData && notificationSettingsData.length) {
      if (!selectedDeals) {
        resetSelectedDeals();
      }

      if (!selectedPortfolios) {
        resetSelectedPortfolios();
      }

      if (!visibleOptions) {
        resetVisibleOptions();
      }
    }
  }, [notificationSettingsData]);

  useEffect((): void => {
    if (isLeaveConfirmed) {
      if (lastLocation) {
        navigate({
          pathname: lastLocation.pathname,
          search: lastLocation.search || '',
        });

        setLastLocation(undefined);
      }

      setIsLeaveConfirmed(false);
    }
  }, [isLeaveConfirmed]);

  useServerFormErrors(serverFormErrors, (): void => {
    submitForm();
  });

  useEditModeQuery(isEditModeQueryDisabled);

  useFormBlock(onFormLeave, isFormChanged, isFormStateChanged, isLeaveConfirmed, isLeaveConfirmModalOpen);

  return (
    <Form id={FORM_ID_SETTINGS_NOTIFICATIONS} onChange={onFormChange}>
      <PullToRefresh isDisabled={isEditMode} onRefresh={onRefresh}>
        <ContentHeader title={pageTitle} />
        <SettingsPageTabs
          isDisabled={isEditMode}
          isCompressed={isRightDrawerOpen}
          activeTab={TAB_NAME_NOTIFICATIONS}
          currentUser={currentUser}
          onBeforeLocationChange={onBeforeLocationChange}
        />
        {
          !isDataLoaded && (
            <Loader isPageLoader withTopOffset />
          )
        }
        {
          isDataLoaded && (
            <Fragment>
              <Controls
                isSaveDisabled={isSaveDisabled}
                editPermissions={settingsNotificationEditPermissions}
                onCancelForm={onCancelForm}
                onSubmitForm={onSubmitForm}
              />
              <LimitedPageContainer isActive>
                <SettingsUserNotifications
                  isCurrentUser
                  isSaving={isFormSaving}
                  isSaveDisabled={isSaveDisabled}
                  isEditMode={isEditMode}
                  isRightDrawerOpen={isRightDrawerOpen}
                  isFormChanged={isFormChanged}
                  notificationSettingsData={notificationSettingsData}
                  notificationTypeSettingsData={notificationTypeSettingsData}
                  commentTypeSettingsData={commentTypeSettingsData}
                  dealList={dealList}
                  portfolioList={portfolioList}
                  selectedDeals={selectedDeals}
                  selectedPortfolios={selectedPortfolios}
                  visibleOptions={visibleOptions}
                  formError={formError}
                  onCancelForm={onCancelForm}
                  onSubmitForm={onSubmitForm}
                  onOptionDealsChange={onOptionDealsChange}
                  onOptionPortfoliosChange={onOptionPortfoliosChange}
                  onDealSearch={onDealSearch}
                  onDealSearchClose={onDealSearchClose}
                  onVisibleOptionsChange={onVisibleOptionsChange}
                />
              </LimitedPageContainer>
            </Fragment>
          )
        }
        {
          (!!formError || !!mainServerFormError) && !isFormSaving && (
            <FormError
              withShowError={isEditMode}
              withLeftOffset={isRightDrawerOpen}
              error={formError || mainServerFormError}
            />
          )
        }
      </PullToRefresh>
      <LeaveConfirmModal
        isOpen={isLeaveConfirmModalOpen}
        onConfirm={onLeaveConfirm}
        onCloseModal={closeLeaveConfirmModal}
      />
      <StatusBar isOpen={isOpenMessage} message={statusMessage} onClose={closeMessage} />
    </Form>
  );
});

const initialValues: FormValues = {};
const mapState: ReduxMapStateCallback = (reducers: Reducers): ReduxMapState => {
  const {
    app,
    controls,
    rightDrawer,
    settings,
  } = reducers;

  const notificationSettingsData: Setting[] = settings.settingsData && settings.settingsData.notificationSettingsData;
  const notificationTypeSettingsData: Setting[] = settings.settingsData
    && settings.settingsData.notificationTypeSettingsData;
  const commentTypeSettingsData: Setting[] = settings.settingsData && settings.settingsData.commentTypeSettingsData;
  const dealList: Deal[] = settings.settingsData && settings.settingsData.dealList;
  const portfolioList: Portfolio[] = settings.settingsData && settings.settingsData.portfolioList;

  if (notificationSettingsData && notificationSettingsData.length) {
    initialValues.notificationSettings = {};

    notificationSettingsData.forEach((settingItem: Setting): void => {
      initialValues.notificationSettings[settingItem.name] = {
        value: settingItem.value,
        options: settingItem.options
        && (settingItem.options.dealIds || settingItem.options.portfolioIds) ? {} : null,
      };

      if (initialValues.notificationSettings[settingItem.name].options) {
        if (settingItem.options.dealIds) {
          initialValues.notificationSettings[settingItem.name].options.dealIds = settingItem
            .options.dealIds.map((dealId: number): Deal | null => (
              dealList && dealList.length ? (
                dealList.find((dealItem: Deal): boolean => dealItem.id === dealId) || null
              ) : null
            ));
        }

        if (settingItem.options.portfolioIds) {
          initialValues.notificationSettings[settingItem.name].options.portfolioIds = settingItem
            .options.portfolioIds.map((portfolioId: number): Portfolio | null => (
              portfolioList && portfolioList.length ? (
                portfolioList.find((portfolioItem: Portfolio): boolean => portfolioItem.id === portfolioId) || null
              ) : null
            ));
        }
      }
    });
  }

  if (notificationTypeSettingsData && notificationTypeSettingsData.length) {
    initialValues.notificationTypeSettings = {};

    notificationTypeSettingsData.forEach((notificationTypeSetting: Setting): void => {
      initialValues.notificationTypeSettings[notificationTypeSetting.name] = {
        value: notificationTypeSetting.value,
      };
    });
  }

  if (commentTypeSettingsData && commentTypeSettingsData.length) {
    initialValues.commentTypeSettings = {};

    commentTypeSettingsData.forEach((commentTypeSetting: Setting): void => {
      initialValues.commentTypeSettings[commentTypeSetting.name] = {
        value: commentTypeSetting.value,
      };
    });
  }

  return {
    isDataLoaded: !!settings.settingsData,
    isEditMode: controls.isEditMode,
    isRightDrawerOpen: rightDrawer.isDrawerOpen,
    currentUser: app.baseData && app.baseData.currentUser,
    notificationSettingsData: settings.settingsData && settings.settingsData.notificationSettingsData,
    notificationTypeSettingsData: settings.settingsData && settings.settingsData.notificationTypeSettingsData,
    commentTypeSettingsData: settings.settingsData && settings.settingsData.commentTypeSettingsData,
    dealList: settings.settingsData && settings.settingsData.dealList,
    portfolioList: settings.settingsData && settings.settingsData.portfolioList,
  };
};

const SettingsNotificationsForm: ReactComponent = (props: ReactComponentProps): ReactElement => {
  const formErrors: ValidationErrors = useSelector(({ settings }: Reducers): ValidationErrorsMap => (
    settings.formErrors
  ));
  const isFormSaving: ValidationErrors = useSelector(({ settings }: Reducers): ValidationErrorsMap => (
    settings.isFormSaving
  ));

  return (
    <FormContainer
      formId={FORM_ID_SETTINGS_NOTIFICATIONS}
      component={SettingsNotifications}
      initialValues={initialValues}
      formErrors={formErrors}
      isSubmitting={isFormSaving}
      {...props}
    />
  );
};

export default connect(mapState)(SettingsNotificationsForm);
